import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { RoutePath } from '@common/enums';
import { DownloadToPSTFileBaseModalData } from '@common/models';
import { GetAppendButtonsIdPipe } from '@common/pipes';
import { GetAppendButtonsIconPipe } from '@common/pipes/get-append-buttons-icon.pipe';
import { ArchivePasswordService } from '@common/services';
import { AbilityService } from '@common/services/auth/ability.service';
import { AuthContextService } from '@common/services/auth/auth-context.service';
import { confirmPasswordValidator, getErrorText, passwordTypeFactory, PasswordTypeKey, togglePasswordType } from '@common/utils';
import { I18NextPipe } from 'angular-i18next';
import { I18_NAMESPACE_APPS_UI } from 'i18n';
import camelCase from 'lodash/camelCase';
import {
  AutoFocusDirective,
  ButtonComponent,
  FormGroupComponent,
  InputButton,
  InputComponent,
  MbsValidators,
  ModalComponent,
  ModalFooterDirective
} from 'mbs-ui-kit';
import { IconSpriteModule } from 'ng-svg-icon-sprite';
import { EMPTY } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { FormKey, FormStructure } from './download-to-pst-file.model';

const components = [ButtonComponent, FormGroupComponent, InputComponent, ModalComponent];
const directives = [AutoFocusDirective, ModalFooterDirective, RouterLink];
const modules = [IconSpriteModule, ReactiveFormsModule];
const pipes = [GetAppendButtonsIconPipe, GetAppendButtonsIdPipe, I18NextPipe];

@Component({
  imports: [...components, ...directives, ...modules, ...pipes],
  templateUrl: './download-to-pst-file.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DownloadToPstFileComponent implements OnInit {
  private ability = inject(AbilityService);
  private archivePasswordService = inject(ArchivePasswordService);
  private authContext = inject(AuthContextService);
  private destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);
  private i18nPipe = inject(I18NextPipe);

  private readonly baseModal = viewChild.required(ModalComponent);

  protected readonly RoutePath = RoutePath;
  protected readonly moduleButton = I18_NAMESPACE_APPS_UI.button;
  protected readonly FormKey = FormKey;
  protected readonly PasswordTypeKey = PasswordTypeKey;
  protected readonly passwordTypeMap = passwordTypeFactory(PasswordTypeKey);

  protected formGroup!: FormGroup<FormStructure>;

  protected readonly prefix = this.authContext.prefix;
  protected readonly domainName = this.authContext.domainName;

  protected readonly isSingleUser = this.ability.isSingleUser;
  protected readonly isLimitedAdmin = this.ability.isLimitedAdmin;
  protected readonly isAdmin = this.ability.isAdmin;

  protected readonly loading = signal<boolean>(false);
  protected readonly buttonLoading = signal<boolean>(false);

  protected readonly isAnyAdmin = computed<boolean>(() => this.isAdmin() || this.isSingleUser() || this.isLimitedAdmin());
  protected readonly passwordType = this.passwordTypeMap[PasswordTypeKey.Base];
  protected readonly confirmPasswordType = this.passwordTypeMap[PasswordTypeKey.Confirm];

  protected readonly baseModalData = computed<DownloadToPSTFileBaseModalData>(() => this.baseModal().data);
  protected readonly countRequired = computed<string>(() => this.baseModalData().countRequired);
  protected readonly id = computed<string>(() => this.baseModalData().id);
  protected readonly taskHasStarted = computed<boolean>(() => this.baseModalData().taskHasStarted);

  ngOnInit(): void {
    this.initForm();
    this.initStreams();
  }

  private initForm(): void {
    this.formGroup = this.fb.group(
      {
        [FormKey.Password]: ['', [Validators.required, MbsValidators.passwordValidator, Validators.maxLength(20)]],
        [FormKey.ConfirmPassword]: ['', [Validators.required]]
      },
      {
        validators: [confirmPasswordValidator(FormKey.Password, FormKey.ConfirmPassword)]
      }
    );
  }

  private initStreams(): void {
    const func = this.baseModalData().func;

    if (!func) return;

    this.loading.set(true);
    func()
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  handleSave(): void {
    const { password } = this.formGroup.value;
    const request$ = this.isAnyAdmin() ? this.archivePasswordService.exportSettings(this.id(), password) : EMPTY;

    this.buttonLoading.set(true);
    request$.pipe(finalize(() => this.buttonLoading.set(false))).subscribe({
      next: () => this.baseModal().save(true),
      error: (res: HttpErrorResponse) => this.handleFormError(res, this.formGroup) // it was added for a case if API return error after saving password, but that's unlikely
    });
  }

  private handleFormError(res: HttpErrorResponse, form: FormGroup<FormStructure>): void {
    const somethingWrongText = this.i18nPipe.transform('common.validation.somethingWrong', { format: 'capitalize' });

    if (res.status === 400) {
      form.get(FormKey.Password).setValue('');
      form.get(FormKey.Password).setErrors({
        [`Password Incorrect`]: { message: getErrorText(res, somethingWrongText) }
      });
    } else {
      form.reset();
      form.setErrors({
        password: { message: somethingWrongText }
      });
    }
  }

  handleClose(): void {
    this.baseModal().close();
  }

  handleChangePasswordType(event: InputButton): void {
    if (!event) return;

    const id = camelCase(event.id.replace('append', ''));
    const passwordType = this.passwordTypeMap[id];

    if (!passwordType) {
      throw new Error(`Caught error manually: Signal with id "${id}" not found`);
    }

    passwordType.update(togglePasswordType);
  }
}
